import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { css } from '@emotion/css'
import {
  LegendOptions,
  Options,
  XAxisOptions,
  AxisTickPositionsArray,
  SeriesOptionsType,
  TooltipOptions
} from 'highcharts'
import { useIntl } from 'react-intl'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Chart } from '../../legacy-design-system/charts'
import {
  fieldIndex,
  getCategorySeries,
  getValueAtX,
  RESOLUTION_TO_TICK_INTERVAL_MAP,
  TIMELINE_RESOLUTIONS
} from './utils-timeline'

// should should probably exist in vuln lib constants
export const VULN_STATES = {
  // open states
  open: 'vulnerabilities-open',
  confirmed: 'vulnerabilities-confirmed',
  reported: 'vulnerabilities-reported',
  suspicious: 'vulnerabilities-suspicious',
  // closed states
  closed: 'vulnerabilities-closed',
  fixed: 'vulnerabilities-fixed',
  notAProblem: 'vulnerabilities-not-a-problem',
  remediated: 'vulnerabilities-remediated',
  autoRemediated: 'vulnerabilities-auto-remediated'
}
const chartTooltip: TooltipOptions = {
  padding: 16,
  borderWidth: 0,
  borderRadius: 6,
  outside: true,
  backgroundColor: theme.color.neutral90,
  shadow: false,
  useHTML: true,
  style: {
    fontSize: '12px',
    color: theme.color.white
  }
}
const chartBoxLegend: LegendOptions = {
  enabled: true,
  layout: 'horizontal',
  align: 'left',
  verticalAlign: 'bottom',
  x: 32, // align with left side of chart instead of axis start
  padding: 0,
  itemDistance: 16,
  symbolRadius: 2,
  symbolPadding: 2,
  itemStyle: {
    color: theme.color.neutral80,
    fontSize: '12px',
    fontWeight: 'normal'
  }
}
// highcharts legends match the series type, this is used to override spline symbol with a box
const squareLegend: SeriesOptionsType = {
  id: 'square-legend',
  type: 'area',
  data: []
}
const markerStyles = {
  symbol: 'circle',
  radius: 3,
  lineWidth: 2,
  fillColor: 'white'
}

// this would be a nice static fn on theme
const setThemeColorOpacity = (color, opacity) => {
  return color.replace(/,\d+\)/i, `,${opacity})`)
}

const mapSeriesValues = (it) => ({
  x: it[fieldIndex.x],
  y: it[fieldIndex.count]
})

export const VulnsOpenClosedChartTooltip = ({
  intl,
  openSeries,
  closedSeries,
  x
}) => {
  const renderSeries = (categorySeries, titleState) => (
    <>
      <div
        className={css`
          font-weight: bold;
        `}
      >
        {`
          ${getValueAtX(categorySeries[titleState].values, x)}
          ${categorySeries[titleState].label}
          ${intl.formatMessage({ id: 'title_vulnerabilities' })}
        `}
      </div>
      <ul
        className={css`
          margin-top: 2px;
          padding-inline-start: 16px;
        `}
      >
        {Object.keys(categorySeries).map((key) => {
          const series = categorySeries[key]

          // skip keys equal to titleState, since they are displayed as the title above
          return key === titleState ? null : (
            <li
              key={key}
              className={css`
                padding: 2px;
              `}
            >
              {`${getValueAtX(series.values, x)} ${series.label}`}
            </li>
          )
        })}
      </ul>
    </>
  )

  return (
    <div>
      {renderSeries(openSeries, VULN_STATES.open)}
      {renderSeries(closedSeries, VULN_STATES.closed)}
    </div>
  )
}

interface Timeline {
  id: string
  name: string
  fields: string[]
  values: number[][]
}
interface VulnsOpenClosedChartProps {
  height: number
  stride: number
  resolution: string
  timeline?: Timeline[]
}
export const VulnsOpenClosedChart = ({
  height,
  stride,
  resolution,
  timeline
}: VulnsOpenClosedChartProps) => {
  const intl = useIntl()
  const openSeries = getCategorySeries(
    [
      VULN_STATES.open,
      VULN_STATES.confirmed,
      VULN_STATES.reported,
      VULN_STATES.suspicious
    ],
    [
      intl.formatMessage({ id: 'title_vulnerabilityStatusOpen' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusConfirmed' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusReported' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusSuspicious' })
    ],
    timeline
  )
  const closedSeries = getCategorySeries(
    [
      VULN_STATES.closed,
      VULN_STATES.fixed,
      VULN_STATES.notAProblem,
      VULN_STATES.remediated,
      VULN_STATES.autoRemediated
    ],
    [
      intl.formatMessage({ id: 'title_vulnerabilityStatusClosed' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusFixed' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusNotProblem' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusRemediated' }),
      intl.formatMessage({ id: 'title_vulnerabilityStatusAutoRemediated' })
    ],
    timeline
  )

  const chartOptions: Options = {
    chart: { height },
    plotOptions: {
      series: {
        animation: false
      }
    },
    tooltip: {
      ...chartTooltip,
      // we need highcharts to set the this context here
      // eslint-disable-next-line object-shorthand
      formatter: function () {
        return ReactDOMServer.renderToStaticMarkup(
          <VulnsOpenClosedChartTooltip
            intl={intl}
            openSeries={openSeries}
            closedSeries={closedSeries}
            x={this.x}
          />
        )
      }
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    xAxis: {
      crosshair: { color: theme.color.neutral90 },
      minPadding: 0,
      type: 'datetime',
      tickLength: 0,
      minTickInterval: RESOLUTION_TO_TICK_INTERVAL_MAP[resolution] * stride,
      // we need highcharts to set the this context here
      // eslint-disable-next-line object-shorthand
      tickPositioner: function () {
        const {
          options: { minTickInterval = 0 },
          max
        } = this
        let { min } = this
        const { series } = this

        // this will never happen but typscript requires it
        if (
          min === null ||
          max === null ||
          minTickInterval === null ||
          series === null
        ) {
          return []
        }

        /*
        NOTE: Monthly resolution does not have a standard interval length because the API data returned uses the
        beginning of the month for the "since" point and the end of the month for the "until" point.  Consequently, the
        intervals could be 28, 29, 30, or 31 days.  For the MONTHLY resolution, the minTickInterval is 28 days (in
        milliseconds), but it is necessary that we calculate the appropriate sized intervals for the x-axis label
        positioning and content (otherwise we end up with, for example, 2 March labels that are uncentered with respect
        to the tick mark instead of a February and a March).  The API data comes in reverse order, youngest to oldest
        month, and the "min" value (which is at the graph's origin point) is set to the until data of the oldest month.
        So, we need to calculate the monthly intervals from the second-oldest month forward.
         */
        const ticks: AxisTickPositionsArray = []
        let data: any = []
        if ('data' in series[0].options) {
          data = series[0].options.data
        }
        let calculatedTotalRange = 0
        let index = data.length - 2

        while (min < max) {
          ticks.push(min)
          const thisInterval =
            resolution === TIMELINE_RESOLUTIONS.MONTHLY && index >= 0
              ? data[index].x - data[index + 1].x
              : minTickInterval
          min += thisInterval
          calculatedTotalRange += thisInterval
          index--
        }

        ticks.info = {
          unitRange: minTickInterval,
          count: stride,
          higherRanks: [],
          totalRange: calculatedTotalRange
        }

        return ticks
      },
      labels: {
        padding: 0,
        align: 'center',
        // we need highcharts to set the this context here
        // eslint-disable-next-line object-shorthand
        formatter: function () {
          return intl
            .formatDate(
              this.value,
              resolution === TIMELINE_RESOLUTIONS.MONTHLY
                ? { year: '2-digit', month: 'short' }
                : { month: 'short', day: 'numeric' }
            )
            .toUpperCase()
        }
      }
    } as XAxisOptions,
    yAxis: {
      gridLineColor: theme.color.neutral40,
      tickInterval: 10
    },
    legend: chartBoxLegend,
    series: [
      {
        type: 'areaspline',
        id: openSeries[VULN_STATES.open].id,
        name: openSeries[VULN_STATES.open].label,
        data: openSeries[VULN_STATES.open].values.map(mapSeriesValues),
        lineWidth: 2,
        color: theme.color.neutral50,
        lineColor: theme.color.neutral50,
        fillColor: {
          linearGradient: {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 1
          },
          stops: [
            [0, setThemeColorOpacity(theme.color.neutral50, '0.5')],
            [1, 'transparent']
          ]
        },
        marker: {
          ...markerStyles,
          lineColor: theme.color.neutral50
        }
      },
      {
        type: 'spline',
        id: closedSeries[VULN_STATES.closed].id,
        name: closedSeries[VULN_STATES.closed].label,
        data: closedSeries[VULN_STATES.closed].values.map(mapSeriesValues),
        color: theme.color.green50,
        marker: {
          ...markerStyles,
          lineColor: theme.color.green50
        },
        // legend fix
        linkedTo: squareLegend.id,
        showInLegend: false
      },
      {
        ...squareLegend,
        name: closedSeries[VULN_STATES.closed].label,
        color: theme.color.green50
      }
    ]
  }

  return <Chart testId="vulns-open-closed-chart" options={chartOptions} />
}
